/**
 * LeaveBalance — the three tiles from "Leave Apply Flow" 5a.
 * @file src/web/features/leave/components/LeaveBalance.tsx
 *
 * @llm-rule WHEN: Anyone who can apply for leave opens the Leave screen
 * @llm-rule NOTE: The numbers are what is LEFT, not what was taken — that is
 *   the question someone actually has when they open this screen. Pending days
 *   are already subtracted, so the figure is what you could still ask for
 *   today, and the tile says so underneath.
 */

export interface Balance {
  type: string;
  entitled: number;
  used: number;
  pending: number;
  remaining: number;
}

const LABEL: Record<string, string> = {
  CASUAL: 'Casual left',
  SICK: 'Sick left',
};

function Tile({
  value,
  label,
  detail,
  dashed,
  tone,
}: {
  value: number;
  label: string;
  detail?: string;
  dashed?: boolean;
  tone?: string;
}) {
  return (
    <div
      className="flex flex-1 flex-col rounded-[12px] px-3.5 py-3"
      style={{
        border: dashed ? '2px dashed var(--rule)' : '2px solid var(--ink)',
        background: dashed ? 'transparent' : 'var(--paper)',
      }}
    >
      <span
        className="font-clock text-[22px] font-bold leading-none"
        style={{ color: tone ?? 'var(--ink)' }}
      >
        {value}
      </span>
      <span className="mt-1.5 text-[12.5px] leading-tight text-ink-faint">{label}</span>
      {detail && <span className="mt-0.5 text-[10.5px] leading-tight text-ink-ghost">{detail}</span>}
    </div>
  );
}

export function LeaveBalance({
  balances,
  pendingRequests,
  year,
}: {
  balances: Balance[];
  pendingRequests: number;
  year: number;
}) {
  return (
    <div>
      <div className="flex gap-2">
        {balances.map((b) => (
          <Tile
            key={b.type}
            value={b.remaining}
            label={LABEL[b.type] ?? `${b.type.toLowerCase()} left`}
            detail={`of ${b.entitled} · ${b.used} taken`}
            // Running out is worth seeing before you start filling the form.
            tone={b.remaining === 0 ? 'var(--dot-flag)' : undefined}
          />
        ))}

        <Tile
          value={pendingRequests}
          label="Pending"
          detail={pendingRequests === 0 ? 'nothing waiting' : 'awaiting a decision'}
          dashed
        />
      </div>

      <p className="mt-2 text-[11px] text-ink-ghost">
        Entitlement resets each January · showing {year}. Unpaid leave has no limit.
      </p>
    </div>
  );
}
